import { realtimeDbService as dbService } from './realtimeDbService';
import { storageService } from './storageService';
import { DomainService } from './domainService';
import { CorporateServiceItem, TestimonialItem, ReferenceItem } from '../types';

export interface DeleteResult {
  success: boolean;
  error?: string;
}

export class DeleteService {
  /**
   * Delete Media Asset (removes from RTDB and Firebase Storage)
   */
  public static async deleteMedia(siteId: string, assetId: string, storagePath?: string): Promise<DeleteResult> {
    try {
      if (!siteId || !assetId) {
        return { success: false, error: 'Site veya medya ID bilgisi eksik.' };
      }

      // 1. Soft-delete / remove in RTDB & local storage
      await dbService.deleteMediaAsset(siteId, assetId);

      // 2. If storage path provided, attempt physical removal in Cloud Storage
      if (storagePath) {
        try {
          await storageService.deleteFile(storagePath);
        } catch (stErr) {
          console.warn('[DeleteService] Cloud storage delete notice:', stErr);
        }
      }

      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteMedia] Error:', err);
      return { success: false, error: err?.message || 'Medya dosyası silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Corporate Service item from site settings and all SERVICES page sections
   */
  public static async deleteService(siteId: string, serviceId: string): Promise<DeleteResult> {
    try {
      const site = await dbService.getSiteById(siteId);
      if (!site) return { success: false, error: 'Site bulunamadı.' };

      const currentList: CorporateServiceItem[] = site.settings?.servicesList || [];
      const updatedList = currentList.filter((s) => s.id !== serviceId);

      // Also update any SERVICES sections across active site pages
      const updatedPages = (site.pages || []).map((p) => {
        const updatedSections = (p?.sections || []).map((sec) => {
          if (sec.type === 'SERVICES') {
            const currentItems = (sec.content?.items || []) as any[];
            return {
              ...sec,
              content: {
                ...sec.content,
                items: currentItems.filter((itm) => itm.id !== serviceId),
              },
            };
          }
          return sec;
        });
        return { ...p, sections: updatedSections };
      });

      const updatedSite = {
        ...site,
        pages: updatedPages,
        settings: {
          ...(site.settings || {}),
          servicesList: updatedList,
        },
        updatedAt: Date.now(),
      };

      await dbService.saveSite(updatedSite);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteService] Error:', err);
      return { success: false, error: err?.message || 'Hizmet silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Testimonial from site settings and TESTIMONIALS page sections
   */
  public static async deleteTestimonial(siteId: string, testimonialId: string): Promise<DeleteResult> {
    try {
      const site = await dbService.getSiteById(siteId);
      if (!site) return { success: false, error: 'Site bulunamadı.' };

      const currentList: TestimonialItem[] = site.settings?.testimonialsList || [];
      const updatedList = currentList.filter((t) => t.id !== testimonialId);

      const updatedPages = (site.pages || []).map((p) => {
        const updatedSections = (p?.sections || []).map((sec) => {
          if (sec.type === 'TESTIMONIALS') {
            const currentItems = (sec.content?.items || []) as any[];
            return {
              ...sec,
              content: {
                ...sec.content,
                items: currentItems.filter((itm) => itm.id !== testimonialId),
              },
            };
          }
          return sec;
        });
        return { ...p, sections: updatedSections };
      });

      const updatedSite = {
        ...site,
        pages: updatedPages,
        settings: {
          ...(site.settings || {}),
          testimonialsList: updatedList,
        },
        updatedAt: Date.now(),
      };

      await dbService.saveSite(updatedSite);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteTestimonial] Error:', err);
      return { success: false, error: err?.message || 'Müşteri yorumu silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Reference/Brand from site settings and REFERENCES page sections
   */
  public static async deleteReference(siteId: string, referenceId: string): Promise<DeleteResult> {
    try {
      const site = await dbService.getSiteById(siteId);
      if (!site) return { success: false, error: 'Site bulunamadı.' };

      const currentList: ReferenceItem[] = site.settings?.referencesList || [];
      const updatedList = currentList.filter((r) => r.id !== referenceId);

      const updatedPages = (site.pages || []).map((p) => {
        const updatedSections = (p?.sections || []).map((sec) => {
          if (sec.type === 'REFERENCES') {
            const currentItems = (sec.content?.items || []) as any[];
            return {
              ...sec,
              content: {
                ...sec.content,
                items: currentItems.filter((itm) => itm.id !== referenceId),
              },
            };
          }
          return sec;
        });
        return { ...p, sections: updatedSections };
      });

      const updatedSite = {
        ...site,
        pages: updatedPages,
        settings: {
          ...(site.settings || {}),
          referencesList: updatedList,
        },
        updatedAt: Date.now(),
      };

      await dbService.saveSite(updatedSite);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteReference] Error:', err);
      return { success: false, error: err?.message || 'Referans silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Yacht from fleet
   */
  public static async deleteYacht(siteId: string, yachtId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteYacht(siteId, yachtId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteYacht] Error:', err);
      return { success: false, error: err?.message || 'Yat kaydı silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Yacht Route
   */
  public static async deleteRoute(siteId: string, routeId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteRoute(siteId, routeId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteRoute] Error:', err);
      return { success: false, error: err?.message || 'Rota kaydı silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Marina
   */
  public static async deleteMarina(siteId: string, marinaId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteMarina(siteId, marinaId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteMarina] Error:', err);
      return { success: false, error: err?.message || 'Marina kaydı silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Reservation record
   */
  public static async deleteReservation(siteId: string, reservationId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteReservation(siteId, reservationId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteReservation] Error:', err);
      return { success: false, error: err?.message || 'Rezervasyon kaydı silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Blog Post
   */
  public static async deleteBlogPost(siteId: string, postId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteBlogPost(siteId, postId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteBlogPost] Error:', err);
      return { success: false, error: err?.message || 'Blog yazısı silinirken bir hata oluştu.' };
    }
  }

  /**
   * Delete a Custom Domain mapping
   */
  public static async deleteDomain(siteId: string, domainName: string): Promise<DeleteResult> {
    try {
      await DomainService.removeDomain(siteId, domainName);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteDomain] Error:', err);
      return { success: false, error: err?.message || 'Alan adı bağlantısı kaldırılırken hata oluştu.' };
    }
  }

  /**
   * Permanently delete a website (removes all database records, storage files, releases website quota, preserves subscription & audit logs)
   */
  public static async deleteSite(siteId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteSite(siteId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteSite] Error:', err);
      return { success: false, error: err?.message || 'Web sitesi silinirken hata oluştu.' };
    }
  }

  /**
   * Delete a contact communication message
   */
  public static async deleteCommunication(siteId: string, messageId: string): Promise<DeleteResult> {
    try {
      await dbService.deleteCommunication(siteId, messageId);
      return { success: true };
    } catch (err: any) {
      console.error('[DeleteService.deleteCommunication] Error:', err);
      return { success: false, error: err?.message || 'Mesaj silinirken hata oluştu.' };
    }
  }
}
